import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { Plus, Trash2, Play, RotateCw, ArrowLeft, Users, PartyPopper } from 'lucide-react';
import SpinWheel from '../components/SpinWheel';

const PLAYER_COLORS = ['#8B6FA3', '#D48C70', '#6B8F71', '#E6B981', '#B794C7', '#5B9BD5', '#EC4899', '#F59E0B'];
const MAX_PLAYERS = 8;
const MIN_PLAYERS = 2;

const TRANSLATIONS = {
  fr: {
    tagline: 'Le jeu de l\u2019addition',
    title1: 'Qui Paye',
    title2: '?',
    intro: 'Entre 2 et 8 joueurs. Entrez vos noms et laissez la roue d\u00e9cider qui r\u00e9gale ce soir !',
    playersLabel: 'Les joueurs',
    playerPlaceholder: (i) => `Nom du joueur ${i}`,
    addPlayer: 'Ajouter un joueur',
    start: 'Lancer le jeu',
    minMsg: 'Minimum 2 joueurs avec un nom rempli',
    roundInProgress: 'Tourn\u00e9e en cours',
    roundIntro: (n) => `${n} joueurs pr\u00eats \u00e0 d\u00e9couvrir leur destin...`,
    spinBtn: '\ud83c\udfb0 Tourner la roue',
    spinning: '\ud83c\udfb2 La roue tourne...',
    restart: 'Recommencer avec d\u2019autres joueurs',
    resultLabel: 'Et le perdant est...',
    resultVerdict: 'paye l\u2019addition ce soir !',
    resultTagline: 'Allez, sois beau joueur \ud83d\ude0e\ud83c\udf55',
    replay: 'Rejouer',
    changePlayers: 'Changer de joueurs',
    backToSite: 'Retour au site',
    footer: (biz) => `\ud83c\udfae Mini-jeu offert par ${biz}`,
  },
  en: {
    tagline: 'The bill game',
    title1: 'Who Pays',
    title2: '?',
    intro: 'Between 2 and 8 players. Enter your names and let the wheel decide who pays tonight!',
    playersLabel: 'Players',
    playerPlaceholder: (i) => `Player ${i} name`,
    addPlayer: 'Add a player',
    start: 'Start the game',
    minMsg: 'Minimum 2 players with a filled name',
    roundInProgress: 'Round in progress',
    roundIntro: (n) => `${n} players ready to discover their fate...`,
    spinBtn: '\ud83c\udfb0 Spin the wheel',
    spinning: '\ud83c\udfb2 The wheel is spinning...',
    restart: 'Restart with other players',
    resultLabel: 'And the loser is...',
    resultVerdict: 'pays the bill tonight!',
    resultTagline: 'Come on, be a good sport \ud83d\ude0e\ud83c\udf55',
    replay: 'Play again',
    changePlayers: 'Change players',
    backToSite: 'Back to site',
    footer: (biz) => `\ud83c\udfae Mini-game offered by ${biz}`,
  },
  nl: {
    tagline: 'Het rekeningspel',
    title1: 'Wie Betaalt',
    title2: '?',
    intro: 'Tussen 2 en 8 spelers. Vul jullie namen in en laat het rad bepalen wie vanavond betaalt!',
    playersLabel: 'Spelers',
    playerPlaceholder: (i) => `Naam van speler ${i}`,
    addPlayer: 'Speler toevoegen',
    start: 'Start het spel',
    minMsg: 'Minimum 2 spelers met een ingevulde naam',
    roundInProgress: 'Ronde bezig',
    roundIntro: (n) => `${n} spelers klaar om hun lot te ontdekken...`,
    spinBtn: '\ud83c\udfb0 Draai het rad',
    spinning: '\ud83c\udfb2 Het rad draait...',
    restart: 'Herstart met andere spelers',
    resultLabel: 'En de verliezer is...',
    resultVerdict: 'betaalt vanavond de rekening!',
    resultTagline: 'Kom op, wees een goede sportman \ud83d\ude0e\ud83c\udf55',
    replay: 'Opnieuw spelen',
    changePlayers: 'Wissel spelers',
    backToSite: 'Terug naar site',
    footer: (biz) => `\ud83c\udfae Mini-spel aangeboden door ${biz}`,
  },
};

const LANGS = [
  { code: 'fr', flag: '\ud83c\uddeb\ud83c\uddf7' },
  { code: 'en', flag: '\ud83c\uddec\ud83c\udde7' },
  { code: 'nl', flag: '\ud83c\uddf3\ud83c\uddf1' },
];

export default function QuiPayePage() {
  const { slug } = useParams();
  const [lang, setLang] = useState(() => localStorage.getItem('qui_paye_lang') || 'fr');
  const T = TRANSLATIONS[lang] || TRANSLATIONS.fr;
  const changeLang = (code) => { setLang(code); localStorage.setItem('qui_paye_lang', code); };
  const [players, setPlayers] = useState([{ id: 'p1', name: '' }, { id: 'p2', name: '' }]);
  const [phase, setPhase] = useState('setup');
  const [winner, setWinner] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const wheelRef = useRef(null);

  const validPlayers = players.map(p => p.name.trim()).filter(Boolean);
  const canStart = validPlayers.length >= MIN_PLAYERS;

  const genPlayerId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

  const updatePlayer = (i, v) => {
    setPlayers(prev => prev.map((p, idx) => idx === i ? { ...p, name: v } : p));
  };
  const addPlayer = () => { if (players.length < MAX_PLAYERS) setPlayers([...players, { id: genPlayerId(), name: '' }]); };
  const removePlayer = (i) => { if (players.length > MIN_PLAYERS) setPlayers(players.filter((_, idx) => idx !== i)); };

  const slots = validPlayers.map((name, i) => ({
    label: name,
    color: PLAYER_COLORS[i % PLAYER_COLORS.length],
  }));

  const startGame = () => { setPhase('play'); setWinner(null); };

  const handleSpin = () => {
    if (spinning || !wheelRef.current) return;
    const targetIdx = Math.floor(Math.random() * slots.length);
    setSpinning(true);
    setTimeout(() => {
      wheelRef.current.spin(targetIdx);
    }, 50);
    // Wait for animation to complete then show winner
    setTimeout(() => {
      setWinner({ index: targetIdx, name: slots[targetIdx].label, color: slots[targetIdx].color });
      setSpinning(false);
      setPhase('result');
    }, 7500);
  };

  const replay = () => { setWinner(null); setPhase('play'); };
  const reset = () => { setWinner(null); setPlayers([{ id: 'p1', name: '' }, { id: 'p2', name: '' }]); setPhase('setup'); };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2D1B3D 0%, #4A2D5E 50%, #5B3370 100%)' }}>
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: '#B794C7' }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: '#D48C70' }} />

      {/* Language flags top-right */}
      <div className="absolute top-5 right-5 z-10 flex items-center gap-1.5 px-2 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20" data-testid="qui-paye-lang-switch">
        {LANGS.map(L => (
          <button
            key={L.code}
            onClick={() => changeLang(L.code)}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-base transition-all ${lang === L.code ? 'bg-white/25 scale-110 shadow-lg' : 'hover:bg-white/10 opacity-70 hover:opacity-100'}`}
            aria-label={L.code}
            data-testid={`qui-paye-lang-${L.code}`}
          >
            {L.flag}
          </button>
        ))}
      </div>

      {/* Back link */}
      {slug && (
        <Link to={`/site/${slug}`} className="absolute top-5 left-5 z-10 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium hover:bg-white/20 transition-all" style={{ fontFamily: 'Outfit' }} data-testid="qui-paye-back">
          <ArrowLeft className="w-4 h-4" /> {T.backToSite}
        </Link>
      )}

      <div className="relative max-w-2xl mx-auto px-4 py-12 sm:py-16">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-4">
            <PartyPopper className="w-4 h-4 text-amber-300" />
            <span className="text-xs font-bold text-white uppercase tracking-widest" style={{ fontFamily: 'Outfit' }}>{T.tagline}</span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-black text-white drop-shadow-2xl mb-3" style={{ fontFamily: "'Playfair Display'" }}>
            {T.title1} <span className="text-amber-300">{T.title2}</span>
          </h1>
          <p className="text-sm sm:text-base text-white/70 max-w-md mx-auto" style={{ fontFamily: 'Outfit' }}>
            {T.intro}
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {/* SETUP PHASE */}
          {phase === 'setup' && (
            <motion.div key="setup" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-2 mb-5">
                <Users className="w-5 h-5 text-amber-300" />
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{T.playersLabel} ({validPlayers.length}/{MAX_PLAYERS})</h2>
              </div>

              <div className="space-y-2.5 mb-5">
                {players.map((player, i) => (
                  <motion.div key={player.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white font-black text-sm shadow-lg" style={{ background: PLAYER_COLORS[i % PLAYER_COLORS.length] }}>
                      {i + 1}
                    </div>
                    <input
                      type="text"
                      value={player.name}
                      onChange={e => updatePlayer(i, e.target.value)}
                      placeholder={T.playerPlaceholder(i + 1)}
                      maxLength={20}
                      className="flex-1 px-4 py-3 rounded-xl bg-white/15 border border-white/20 text-white placeholder-white/40 outline-none focus:bg-white/20 focus:border-amber-300/50 text-sm font-medium transition-all"
                      style={{ fontFamily: 'Outfit' }}
                      data-testid={`qui-paye-player-${i}`}
                    />
                    {players.length > MIN_PLAYERS && (
                      <button onClick={() => removePlayer(i)} className="w-9 h-9 rounded-full flex items-center justify-center text-red-300 hover:bg-red-500/20 transition-all" data-testid={`qui-paye-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>

              {players.length < MAX_PLAYERS && (
                <button
                  onClick={addPlayer}
                  className="w-full flex items-center justify-center gap-2 py-3 mb-5 rounded-xl bg-white/5 border border-dashed border-white/30 text-white/80 hover:bg-white/10 hover:text-white text-sm font-medium transition-all"
                  style={{ fontFamily: 'Outfit' }}
                  data-testid="qui-paye-add-player"
                >
                  <Plus className="w-4 h-4" /> {T.addPlayer}
                </button>
              )}

              <button
                onClick={startGame}
                disabled={!canStart}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-black text-base shadow-2xl active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)', fontFamily: 'Outfit' }}
                data-testid="qui-paye-start"
              >
                <Play className="w-5 h-5" fill="currentColor" /> {T.start}
              </button>
              {!canStart && <p className="text-center text-xs text-amber-300/80 mt-3" style={{ fontFamily: 'Outfit' }}>{T.minMsg}</p>}
            </motion.div>
          )}

          {/* PLAY PHASE */}
          {phase === 'play' && (
            <motion.div key="play" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 shadow-2xl mb-6 w-full">
                <p className="text-center text-xs uppercase tracking-widest font-bold text-amber-300 mb-1" style={{ fontFamily: 'Outfit' }}>{T.roundInProgress}</p>
                <p className="text-center text-sm text-white/80" style={{ fontFamily: 'Outfit' }}>
                  {T.roundIntro(validPlayers.length)}
                </p>
              </div>

              <div className="relative" data-testid="qui-paye-wheel-wrap">
                {/* Pointer */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-3 z-20" style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))' }}>
                  <div style={{
                    width: 0, height: 0,
                    borderLeft: '14px solid transparent',
                    borderRight: '14px solid transparent',
                    borderTop: '26px solid #FFD700',
                  }} />
                </div>
                <SpinWheel ref={wheelRef} slots={slots} size={340} preview={true} onSpinEnd={() => {}} />
              </div>

              <button
                onClick={handleSpin}
                disabled={spinning}
                className="mt-8 px-10 py-4 rounded-full text-white font-black text-lg shadow-2xl active:scale-95 transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)', fontFamily: 'Outfit' }}
                data-testid="qui-paye-spin"
              >
                {spinning ? T.spinning : T.spinBtn}
              </button>

                <button onClick={reset} className="mt-4 text-xs text-white/60 hover:text-white underline transition-all" style={{ fontFamily: 'Outfit' }}>
                {T.restart}
              </button>
            </motion.div>
          )}

          {/* RESULT PHASE */}
          {phase === 'result' && winner && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-2xl text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}>
                <p className="text-xs uppercase tracking-widest font-bold text-amber-300 mb-2" style={{ fontFamily: 'Outfit' }}>{T.resultLabel}</p>
                <div className="w-28 h-28 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-black text-4xl shadow-2xl ring-4 ring-white/30" style={{ background: winner.color, fontFamily: "'Playfair Display'" }}>
                  {winner.name.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-5xl sm:text-6xl font-black text-white drop-shadow-2xl mb-3 break-words" style={{ fontFamily: "'Playfair Display'" }} data-testid="qui-paye-winner">
                  {winner.name}
                </h2>
                <p className="text-lg text-amber-300 font-bold mb-2" style={{ fontFamily: 'Outfit' }}>{T.resultVerdict}</p>
                <p className="text-sm text-white/60 mb-8" style={{ fontFamily: 'Outfit' }}>{T.resultTagline}</p>
              </motion.div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={replay}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-bold text-sm shadow-lg active:scale-95 transition-all"
                  style={{ background: 'linear-gradient(135deg, #8B6FA3, #B794C7)', fontFamily: 'Outfit' }}
                  data-testid="qui-paye-replay"
                >
                  <RotateCw className="w-4 h-4" /> {T.replay}
                </button>
                <button
                  onClick={reset}
                  className="px-6 py-3 rounded-full text-white/80 font-medium text-sm border border-white/30 hover:bg-white/10 active:scale-95 transition-all"
                  style={{ fontFamily: 'Outfit' }}
                  data-testid="qui-paye-reset"
                >
                  {T.changePlayers}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-center text-[10px] text-white/40 mt-10" style={{ fontFamily: 'Outfit' }}>
          {T.footer(slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Spin Review')}
        </p>
      </div>
    </div>
  );
}
